import { bubbleTail, heart } from './icons'
import type { WidgetSettings } from './types'

const PULSE_SEEN_KEY = 'gvfb-chatbot-pulse-seen'

/**
 * The floating speech bubble from the supplied artwork: a gold-outlined bubble with a tail
 * pointing at the corner of the screen it sits in, the two-tone heart, and "Ask me
 * anything!" set over two lines beside it.
 *
 * The pulse is a first-impression device, not a permanent animation. It runs a fixed number
 * of times, stops the moment anyone interacts, is remembered for the rest of the browsing
 * session so it doesn't repeat on every page, and never runs at all for a visitor who has
 * asked for reduced motion.
 */
export class Launcher {
  private readonly el: HTMLButtonElement

  constructor(
    private readonly settings: WidgetSettings,
    private readonly onOpen: () => void,
  ) {
    this.el = document.createElement('button')
    this.el.type = 'button'
    this.el.className = 'launcher'
    this.el.setAttribute('aria-haspopup', 'dialog')
    this.el.setAttribute('aria-expanded', 'false')
    this.el.setAttribute('aria-label', `${settings.launcher_text} Open the chat assistant.`)

    // The artwork sets the text over two lines beside the heart, which is what gives the
    // bubble its shape. The wrapping is left to `text-wrap: balance` against a narrow
    // max-width rather than a hard split, because the text is admin-editable and a rule like
    // "break at the last space" turns any longer wording into one word on a line of its own.
    this.el.innerHTML = `
      ${heart(settings.colour_navy, settings.colour_gold)}
      <span class="launcher-text">${settings.launcher_text.replace(/</g, '&lt;')}</span>
      ${bubbleTail(settings.colour_gold, '#ffffff', settings.position.endsWith('left'))}
    `

    this.el.style.setProperty('--pulse-count', String(settings.pulse_count))

    this.el.addEventListener('click', () => {
      this.stopPulse()
      this.onOpen()
    })

    // Any interaction means we have their attention; the animation has done its job.
    this.el.addEventListener('mouseenter', () => this.stopPulse(), { once: true })
    this.el.addEventListener('focus', () => this.stopPulse(), { once: true })
  }

  element(): HTMLElement {
    return this.el
  }

  setOpen(open: boolean): void {
    this.el.hidden = open
    this.el.setAttribute('aria-expanded', String(open))
  }

  focus(): void {
    this.el.focus()
  }

  maybePulse(): void {
    if (!this.settings.pulse_enabled || this.pulseAlreadySeen()) {
      return
    }

    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      return
    }

    window.setTimeout(() => {
      // Don't start animating something the visitor has already opened.
      if (!this.el.hidden) {
        this.el.classList.add('pulsing')
        this.rememberPulse()
      }
    }, this.settings.pulse_delay_ms)
  }

  private stopPulse(): void {
    this.el.classList.remove('pulsing')
    this.rememberPulse()
  }

  /**
   * sessionStorage, not localStorage: the nudge should come back on a future visit, just not
   * on every page of this one. Both can throw in a locked-down browser, so both are guarded.
   */
  private pulseAlreadySeen(): boolean {
    try {
      return window.sessionStorage.getItem(PULSE_SEEN_KEY) === '1'
    } catch {
      return false
    }
  }

  private rememberPulse(): void {
    try {
      window.sessionStorage.setItem(PULSE_SEEN_KEY, '1')
    } catch {
      // Private browsing or blocked site data — the pulse simply repeats. Not worth failing.
    }
  }
}
